import type {ReactElement} from 'react';
import React, {useEffect} from 'react';
import {BaseShadowThumbnailOptions} from '@Panels/poster-editor-item-shadow-panel/components/base-shadow-thumbnail-options';
import {BaseItemCustomShadowColorOption} from '@Components/poster-editor/components/poster-editing-side-panel/components/base-item-custom-shadow-color-option';
import {BaseItemCustomShadowOpacityOption} from '@Components/poster-editor/components/poster-editing-side-panel/components/base-item-custom-shadow-opacity-option';
import {BaseItemCustomShadowAngleOption} from '@Components/poster-editor/components/poster-editing-side-panel/components/base-item-custom-shadow-angle-option';
import {updateOptionsPanelHeight} from '@Components/poster-editor/library/poster-editor-library';
import {BaseItemCustomShadowDistanceOption} from '@Components/poster-editor/components/poster-editing-side-panel/components/base-item-custom-shadow-distance-option';
import {BaseItemCustomShadowBlurOption} from '@Components/poster-editor/components/poster-editing-side-panel/components/base-item-custom-shadow-blur-option';
import type {ITEM_TYPE} from '@PosterWhiteboard/items/item/item.types';
import type {ShadowOrGlowOptionsObject} from '@Utils/fabric-shadow.util';
import {ShadowType} from '@PosterWhiteboard/poster/poster-backend.types';
import {ShadowOrGlowType} from '@postermywall/fabricjs-2';

interface BaseMobileShadowOptionsProps {
  panelId: string;
  shadowOptions: ShadowOrGlowOptionsObject | null;
  itemType: ITEM_TYPE;
  isForText?: boolean;
  isForVector?: boolean;

  onToggleShadow(newShadowType: ShadowType | null): void;

  updateAngle(dropShadowAngle: number, undoable?: boolean): void;

  updateOpacity(dropShadowAlpha: number, undoable?: boolean): void;

  updateDistance(dropShadowDistance: number, undoable?: boolean): void;

  updateBlur(dropShadowBlur: number, undoable?: boolean): void;

  onClickColorOption(): void;
}

export function BaseItemShadowOptionsMobile({isForText = true, isForVector = false, ...props}: BaseMobileShadowOptionsProps): ReactElement<any> {
  useEffect((): void => {
    updateHeightInRedux(getSelectedShadowType());
  }, [props.shadowOptions?.shadowOrGlowType]);

  const getSelectedShadowType = (): ShadowType => {
    if (!props.shadowOptions) {
      return ShadowType.NONE;
    }

    switch (props.shadowOptions.shadowOrGlowType) {
      case ShadowOrGlowType.CUSTOM_SHADOW:
        return ShadowType.CUSTOM;
      case ShadowOrGlowType.STRONG_SHADOW:
        return ShadowType.STRONG;
      case ShadowOrGlowType.LIGHT_SHADOW:
        return ShadowType.LIGHT;
      case ShadowOrGlowType.LIGHT_GLOW:
      case ShadowOrGlowType.STRONG_GLOW:
      case null:
        return ShadowType.NONE;
      default:
        throw new Error(`Unhandled shadowOrGlowType type: ${props.shadowOptions.shadowOrGlowType} in BaseMobileShadowOptions`);
    }
  };

  const updateHeightInRedux = (type: ShadowType): void => {
    let height: string;

    switch (type) {
      case ShadowType.CUSTOM:
        height = isForText || isForVector ? '516px' : '538px';
        break;
      case ShadowType.LIGHT:
      case ShadowType.STRONG:
        height = isForText || isForVector ? '234px' : '258px';
        break;
      default:
        height = isForText || isForVector ? '158px' : '182px';
    }

    updateOptionsPanelHeight(props.panelId, height);
  };

  const onToggleShadow = (newShadowType: ShadowType | null): void => {
    props.onToggleShadow(newShadowType);
  };

  const getShadowList = (): ReactElement<any> => {
    return (
      <BaseShadowThumbnailOptions
        isForVector={isForVector}
        isForText={isForText}
        itemType={props.itemType}
        selectedType={getSelectedShadowType()}
        onToggleShadow={onToggleShadow}
      />
    );
  };

  const getColorSubOption = (): ReactElement<any> | null => {
    if (!props.shadowOptions) {
      return null;
    }

    return (
      <BaseItemCustomShadowColorOption
        className="spacing-m-b-4"
        dropShadowColor={props.shadowOptions.color}
        onClickColorFill={(): void => {
          props.onClickColorOption();
        }}
      />
    );
  };
  const updateOpacity = (dropShadowAlpha: number, undoable = true): void => {
    props.updateOpacity(dropShadowAlpha, undoable);
  };

  const onUpdateDistance = (dropShadowDistance: number, undoable = true): void => {
    props.updateDistance(dropShadowDistance, undoable);
  };

  const onUpdateBlur = (dropShadowBlur: number, undoable = true): void => {
    props.updateBlur(dropShadowBlur, undoable);
  };

  const updateAngle = (dropShadowAngle: number, undoable = true): void => {
    props.updateAngle(dropShadowAngle, undoable);
  };

  const getOpacitySubOption = (): ReactElement<any> | null => {
    if (!props.shadowOptions) {
      return null;
    }

    return <BaseItemCustomShadowOpacityOption className="spacing-m-b-4" dropShadowAlpha={props.shadowOptions.color[3]} onUpdateOpacity={updateOpacity} />;
  };

  const getDistanceSubOption = (): ReactElement<any> | null => {
    if (!props.shadowOptions) {
      return null;
    }

    return <BaseItemCustomShadowDistanceOption className="spacing-m-b-4" dropShadowDistance={props.shadowOptions.distance} onUpdateDistance={onUpdateDistance} />;
  };

  const getBlurSubOption = (): ReactElement<any> | null => {
    if (!props.shadowOptions) {
      return null;
    }

    return <BaseItemCustomShadowBlurOption className="spacing-m-b-4" dropShadowBlur={props.shadowOptions.blur} onUpdateBlur={onUpdateBlur} />;
  };

  const getAngleSubOption = (): ReactElement<any> | null => {
    if (!props.shadowOptions) {
      return null;
    }

    return <BaseItemCustomShadowAngleOption className="spacing-m-b-4" dropShadowAngle={props.shadowOptions.angle} onUpdateAngle={updateAngle} />;
  };

  const getCustomShadowOptions = (): ReactElement<any> | null => {
    if (getSelectedShadowType() === ShadowType.CUSTOM) {
      return (
        <>
          {getOpacitySubOption()}
          {getAngleSubOption()}
          {getBlurSubOption()}
          {getColorSubOption()}
          {getDistanceSubOption()}
        </>
      );
    }
    if (getSelectedShadowType() === ShadowType.STRONG || getSelectedShadowType() === ShadowType.LIGHT) {
      return <>{getDistanceSubOption()}</>;
    }
    return null;
  };

  const getOptions = (): ReactElement<any> => {
    return (
      <>
        {getCustomShadowOptions()}
        {getShadowList()}
      </>
    );
  };

  const getShadowOptions = (): ReactElement<any> => {
    return <>{getOptions()}</>;
  };
  return getShadowOptions();
}
